// ===========================================
// Lessons API Functions
// ===========================================
// API calls for lesson management endpoints

import { apiClient } from '../services/api';
import type { InvoiceToAmend } from './invoices.api';
import { Teacher, Student } from './users.api';
import { Term, Location, Room, Instrument, LessonType, ProgramLevel, Stream } from './admin.api';

// ===========================================
// TYPES
// ===========================================

export type HybridCombination = 'GROUP_INDIVIDUAL' | 'GROUP_BAND' | 'INDIVIDUAL_BAND';

/**
 * What the server's pattern-week clamp did when a hybrid pattern was written
 * into a term (mirrors backend `services/lesson/hybridPatternBounds.ts:PatternClampWarning`).
 * `dropped`: the term is SHORTER than the pattern — those weeks were not copied.
 * `uncovered`: the term is LONGER — those trailing weeks have no session and
 * were NOT filled in automatically. Either way `message` is the sentence to show.
 */
export interface PatternClampWarning {
  termId: string;
  termName: string;
  termWeeks: number;
  kind: 'dropped' | 'uncovered' | 'removed';
  weeks: number[];
  // Mirrors the backend `WeekBucket` (services/lesson/hybridPatternBounds.ts) — keep in step.
  bucket?: 'groupWeeks' | 'individualWeeks' | 'openClinicWeeks' | 'rehearsalWeeks' | 'classWeeks' | 'bandWeeks' | 'window';
  /** Why a week was dropped by the class window (mid-term start / last week). */
  reason?: 'before_start' | 'after_end';
  message: string;
}

/** A lesson write's payload plus the clamp warnings the server attached. */
export interface LessonWriteResult<T> {
  data: T;
  warnings: PatternClampWarning[];
}

/** An update's result: also the sent invoices that MAY need amending after a window change ([] when it did not change). */
export interface LessonUpdateResult extends LessonWriteResult<Lesson> {
  invoicesToAmend: InvoiceToAmend[];
  /** Roles R17: true for a caller WITHOUT invoice read when the list above would not be empty (it is [] for them). */
  invoicesMayNeedAttention: boolean;
}

/**
 * A student enrolled into a term their family ALREADY holds a live invoice for (mirrors backend
 * `AlreadyInvoicedEnrolment`, services/invoice/amend/findInvoicesToAmend.ts). Term invoicing makes
 * one invoice per family per term and skips a family already invoiced, so nothing bills this child
 * unless the admin does. `invoices` are that family's live invoices for `termId`.
 */
export interface AlreadyInvoicedEnrolment {
  enrollmentId: string;
  lessonId: string;
  studentId: string;
  studentName: string;
  familyId: string;
  familyName: string;
  termId: string;
  termName: string;
  invoices: InvoiceToAmend[];
}

/** A single enrol's result. `alreadyInvoiced` is [] when the backend omits it (stale) or for a user without invoice read. */
export interface EnrollStudentResult {
  enrollment: LessonEnrollment;
  alreadyInvoiced: AlreadyInvoicedEnrolment[];
  /** Roles R17: true for a caller WITHOUT invoice read when the list above would not be empty (it is [] for them). */
  invoicesMayNeedAttention: boolean;
}

/** A bulk enrol's result (see `EnrollStudentResult`). */
export interface BulkEnrollResult {
  enrollments: LessonEnrollment[];
  alreadyInvoiced: AlreadyInvoicedEnrolment[];
  /** Roles R17: true for a caller WITHOUT invoice read when the list above would not be empty (it is [] for them). */
  invoicesMayNeedAttention: boolean;
}

/** A student start-week change's result. */
export interface EnrollmentUpdateResult {
  enrollment: LessonEnrollment;
  invoicesToAmend: InvoiceToAmend[];
  /** Roles R17: true for a caller WITHOUT invoice read when the list above would not be empty (it is [] for them). */
  invoicesMayNeedAttention: boolean;
}
export type BandTermPatternType = 'EVERY_WEEK' | 'CUSTOM';

export interface BandTerm {
  id: string;
  lessonId: string;
  termId: string;
  /** The weeks (`LessonTermWeeks.weeks`). `GET /:id/band-term` sends both keys for one release (D11). */
  weeks?: number[];
  /** @deprecated legacy name of `weeks`; read `weeks ?? rehearsalWeeks`. */
  rehearsalWeeks: number[];
  patternType: BandTermPatternType;
  createdAt: string;
  updatedAt: string;
}

/**
 * The weeks a plain class (INDIVIDUAL, GROUP or BAND) runs in one term — the backend's
 * `LessonTermWeeks` row (table still "BandTerm"). `BandTerm` above is the same row in its
 * legacy shape, sent for one release only (md/plan/individual-group-week-selection.md D11).
 */
export interface LessonTermWeeksRow {
  id: string;
  lessonId: string;
  termId: string;
  weeks: number[];
  patternType: BandTermPatternType;
  createdAt: string;
  updatedAt: string;
}

/** `classWeeks` on create / update / bulk: CUSTOM needs at least one week (the server 400s otherwise). */
export interface ClassWeeksInput {
  patternType: BandTermPatternType;
  weeks: number[];
}

export interface UpsertBandTermInput {
  termId: string;
  rehearsalWeeks: number[];
  patternType: BandTermPatternType;
}

export interface HybridPattern {
  /** Clinic weeks currently opened to any term week (teacher away etc.). */
  openClinicWeeks?: number[];
  id: string;
  lessonId: string;
  termId: string;
  patternType: 'ALTERNATING' | 'CUSTOM';
  hybridCombination: HybridCombination;
  groupWeeks: number[];
  individualWeeks: number[];
  bandWeeks: number[] | null;
  bandLessonId: string | null;
  BandLesson: { id: string; name: string; dayOfWeek: number } | null;
  individualSlotDuration: number;
  bookingDeadlineHours: number;
  bookingsOpen: boolean;
  /** When true, parents may book a week-N clinic into week N+1 (overflow). */
  overflowBookingsOpen: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClassMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'PARENT' | 'STUDENT';
  studentNames?: string[];
  studentName?: string;
}

export interface ClassMembers {
  parents: ClassMember[];
  students: ClassMember[];
}

export interface LessonEnrollment {
  id: string;
  lessonId: string;
  studentId: string;
  enrolledAt: string;
  isActive: boolean;
  /** The week THIS student joins (null = the class's own start). */
  startWeek: number | null;
  Student: Student & {
    /** Present on GET /lessons/:id/enrollments — the family's contact names. */
    Family?: { Parent: LessonEnrollmentParentContact[] } | null;
  };
}

export interface LessonEnrollmentParentContact {
  id: string;
  contact1FirstName: string;
  contact1LastName: string;
  contact2FirstName: string | null;
  contact2LastName: string | null;
  // Present only when the caller holds people_families:read (admins, and staff
  // with that permission) — never for a plain teacher.
  contact1Email?: string;
  contact1Phone?: string;
  contact1Relationship?: string;
  contact2Email?: string | null;
  contact2Phone?: string | null;
  contact2Relationship?: string | null;
}

export interface Lesson {
  id: string;
  schoolId: string;
  lessonTypeId: string;
  termId: string;
  teacherId: string;
  roomId: string;
  instrumentId: string | null;
  programLevelId: string | null;
  streamId: string | null;
  name: string;
  description: string | null;
  hasSchedule: boolean;
  dayOfWeek: number | null;
  startTime: string | null;
  endTime: string | null;
  durationMins: number | null;
  maxStudents: number;
  isRecurring: boolean;
  isActive: boolean;
  /** First week the class runs (null = week 1). */
  startWeek: number | null;
  /** LAST week the class runs, inclusive (null = term end). */
  endWeek: number | null;
  createdAt: string;
  updatedAt: string;
  replacementTeacherId: string | null;
  replacementReason: string | null;
  replacedAt: string | null;
  replacedBy: string | null;
  // Partner classes: the paired lesson id (null = unpaired) + a light shape of the partner.
  partnerLessonId: string | null;
  Partner?: {
    id: string;
    name: string;
    dayOfWeek: number | null;
    startTime: string | null;
    endTime: string | null;
    isActive: boolean;
  } | null;
  LessonType: LessonType;
  Term: Term;
  Teacher: Teacher & {
    User: { id: string; firstName: string; lastName: string };
  };
  Instructor?: {
    id: string;
    color: string | null;
    User: { id: string; firstName: string; lastName: string };
  };
  Room: Room & {
    Location: Pick<Location, 'id' | 'name'>;
  };
  Instrument: Instrument | null;
  ProgramLevel: ProgramLevel | null;
  Stream: Stream | null;
  HybridPattern: HybridPattern | null;
  HybridLessonPattern?: HybridPattern | null;
  BandTerm?: BandTerm[];
  /** Read only through `utils/classWeeks.ts` (pass `?? null` explicitly: the helper requires it). */
  LessonTermWeeks?: LessonTermWeeksRow[];
  LessonEnrollment: LessonEnrollment[];
  ReplacementTeacher: Teacher & {
    User: { id: string; firstName: string; lastName: string };
  } | null;
  _count: { LessonEnrollment: number };
}

export interface LessonFilters {
  termId?: string;
  teacherId?: string;
  roomId?: string;
  instrumentId?: string;
  programLevelId?: string;
  lessonTypeId?: string;
  dayOfWeek?: number;
  isActive?: boolean;
  allClasses?: boolean;
  /**
   * Only the caller's own classes. The teacher view sends it: a caller holding
   * scheduling.read (an ADMIN or a staff role) would otherwise get the whole school.
   * Roles R12, slice 1b §5.12. A login with no teacher record gets a 400.
   */
  mine?: boolean;
}

export interface CreateLessonInput {
  lessonTypeId: string;
  termId: string;
  teacherId: string;
  roomId: string;
  instrumentId?: string;
  programLevelId?: string;
  streamId?: string;
  name: string;
  description?: string;
  hasSchedule?: boolean;
  dayOfWeek?: number;
  startTime?: string;
  endTime?: string;
  durationMins?: number;
  maxStudents: number;
  isRecurring?: boolean;
  /** Mid-term window: first / last week the class runs (1..termWeekCount). Omit for a full term. */
  startWeek?: number;
  endWeek?: number;
  hybridPattern?: {
    patternType: 'ALTERNATING' | 'CUSTOM';
    hybridCombination?: HybridCombination;
    groupWeeks: number[];
    individualWeeks: number[];
    bandLessonId?: string | null;
    individualSlotDuration: number;
    bookingDeadlineHours: number;
  };
  /**
   * The weeks a plain class (INDIVIDUAL / GROUP / BAND) runs in each term: EVERY_WEEK, or a
   * CUSTOM list (fortnightly, say). Replaces the band-only `bandTerm` key, which the server
   * still accepts for one release (D11) — never send both (400).
   */
  classWeeks?: ClassWeeksInput;
  overrideConflicts?: boolean;
  // Teacher time off: admin confirmed the amber "schedule anyway" warning. NARROW — the backend
  // forgives only an approved TEACHER_UNAVAILABLE range; real double-bookings still 409.
  allowAwayOverride?: boolean;
  // Partner classes: pair the new (single-term, INDIVIDUAL) lesson with an existing eligible one on
  // create. The backend validates + symmetric-writes. Only sent for single-term creates.
  partnerLessonId?: string;
}

export interface UpdateLessonInput {
  lessonTypeId?: string;
  termId?: string;
  teacherId?: string;
  roomId?: string;
  instrumentId?: string | null;
  programLevelId?: string | null;
  streamId?: string | null;
  name?: string;
  description?: string | null;
  hasSchedule?: boolean;
  dayOfWeek?: number | null;
  startTime?: string | null;
  endTime?: string | null;
  durationMins?: number | null;
  maxStudents?: number;
  isRecurring?: boolean;
  isActive?: boolean;
  /** number sets, null clears, omitted = unchanged. */
  startWeek?: number | null;
  endWeek?: number | null;
  hybridPattern?: {
    patternType: 'ALTERNATING' | 'CUSTOM';
    hybridCombination?: HybridCombination;
    groupWeeks: number[];
    individualWeeks: number[];
    bandLessonId?: string | null;
    individualSlotDuration: number;
    bookingDeadlineHours: number;
  } | null;
  /** See `CreateLessonInput.classWeeks`. Omitted = unchanged; null deletes the row. */
  classWeeks?: ClassWeeksInput | null;
  excludeLessonIds?: string[]; // Exclude these lessons from conflict checks (for grouped lessons)
  overrideConflicts?: boolean;
  // Teacher time off: see CreateLessonInput — narrow, away-only override.
  allowAwayOverride?: boolean;
  // Partner classes: an id to pair/re-pair, null to unpair, omitted to leave unchanged.
  partnerLessonId?: string | null;
}

export interface BulkCreateLessonsInput {
  termIds: string[];
  // partnerLessonId is omitted: partner pairing is per-term 1:1 and can't map across a multi-term
  // bulk create (the backend also 400s it). Type-enforce the "bulk never pairs" invariant.
  // `allowAwayOverride` IS allowed here (unlike partnerLessonId): "add a term to an existing
  // class" routes through bulk, so excluding it made the amber "Schedule anyway" confirm a dead
  // end for multi-term classes — the update loop succeeded while the added term still 409'd.
  lessonData: Omit<CreateLessonInput, 'termId' | 'partnerLessonId'>;
  excludeLessonIds?: string[]; // Exclude these lessons from conflict checks (when editing)
  overrideConflicts?: boolean;
  /** The ONE term of `termIds` the lessonData start/end week applies to; the rest run the full term. */
  windowTermId?: string;
}

export interface AvailabilityConflict {
  source: ConflictSource;
  sourceId: string;
  lessonId?: string;
  roomId?: string | null;
  date: string; // ISO datetime — serialised BlockingRange.date
  startTime: string;
  endTime: string;
  label: string;
}

export interface AvailabilityResult {
  available: boolean;
  conflict?: AvailabilityConflict;
}

export interface CapacityResult {
  current: number;
  max: number;
  available: number;
}

// ===========================================
// RESCHEDULE TYPES
// ===========================================

export interface ConflictCheckInput {
  newDayOfWeek: number;
  newStartTime: string;
  newEndTime: string;
}

export type ConflictSource =
  | 'LESSON'
  | 'LESSON_EXCEPTION'
  | 'HYBRID_BOOKING'
  | 'EXTRA_LESSON'
  | 'MAKEUP_SLOT';

export interface ConflictCheckResult {
  hasConflicts: boolean;
  teacherConflict: {
    lessonId: string;
    lessonName: string;
    time: string;
    date: string; // ISO YYYY-MM-DD — actual date of the clashing occurrence
    source: ConflictSource;
  } | null;
  roomConflict: {
    lessonId: string;
    lessonName: string;
    time: string;
    date: string;
    source: ConflictSource;
  } | null;
  scheduleConflict: {
    reason: string;
  } | null;
  affectedStudents: number;
  affectedEnrollments: {
    studentId: string;
    studentName: string;
    hasOtherLessons: boolean;
  }[];
}

export interface RescheduleInput {
  newDayOfWeek: number;
  newStartTime: string;
  newEndTime: string;
  notifyParents?: boolean;
  reason?: string;
  applyToAllTerms?: boolean;
  fromWeekNumber?: number;
  overrideConflicts?: boolean;
}

export interface RelatedLesson {
  id: string;
  termId: string;
  termName: string;
}

// ===========================================
// CHANGE LOCATION (ROOM) TYPES
// ===========================================

export interface ChangeLessonRoomInput {
  roomId: string;
  applyToAllTerms?: boolean;
  overrideConflicts?: boolean;
  reason?: string;
  notifyParents?: boolean;
}

/** Per-lesson-row result of checking the target room is free. */
export interface ChangeRoomConflictResult {
  hasConflicts: boolean;
  conflicts: {
    lessonId: string;
    termName: string;
    roomConflict: {
      lessonId: string;
      lessonName: string;
      time: string;
      date: string;
      source: string;
    } | null;
  }[];
}

// ===========================================
// SINGLE-OCCURRENCE TYPES
// ===========================================

export interface SingleOccurrenceConflictCheckInput {
  originalDate: string;
  newDate: string;
  newStartTime: string;
  newEndTime: string;
  newRoomId?: string;
}

export interface SingleOccurrenceRescheduleInput {
  originalDate: string;
  newDate: string;
  newStartTime: string;
  newEndTime: string;
  newRoomId?: string;
  notifyParents?: boolean;
  reason?: string;
  overrideConflicts?: boolean;
  /** Room-only change (date/time unchanged) → sends the location-changed email. */
  isRoomOnly?: boolean;
}

export interface CancelOccurrenceInput {
  originalDate: string;
  reason?: string;
  notifyParents?: boolean;
  activateMakeupBooking?: boolean;
}

/**
 * A dated lesson OCCURRENCE with LessonException applied — mirrors the backend
 * `LessonOccurrence` (services/lesson/occurrences/expandLessonOccurrences.ts).
 *
 * `date` is where the session actually happens. `originalDate` (set only when
 * rescheduled) is the exception's fixed identity key — any write targeting this
 * occurrence must key on that, not on `date`.
 */
export interface LessonOccurrence {
  lessonId: string;
  lessonName: string;
  /** ISO datetime; the occurrence's actual date at UTC midnight. */
  date: string;
  /** `YYYY-MM-DD` — the display/query key. */
  dateKey: string;
  weekNumber: number;
  startTime: string;
  endTime: string;
  roomId: string | null;
  teacherId: string;
  kind: 'REGULAR' | 'HYBRID_GROUP' | 'HYBRID_INDIVIDUAL' | 'BAND_WEEK';
  isCancelled: boolean;
  isRescheduled: boolean;
  originalDate: string | null;
}

export interface LessonOccurrencesParams {
  /** `YYYY-MM-DD`. Required — the backend caps the window at 366 days. */
  startDate: string;
  endDate: string;
  teacherId?: string;
  lessonId?: string;
  /**
   * Opt out of teacher self-scoping to see the whole school's occurrences.
   * Must MIRROR the `allClasses` flag used on the lessons query, or rows for
   * other teachers arrive with no occurrence and silently fall back to the
   * recurring date. Widens to the caller's own school only.
   */
  allClasses?: boolean;
  /** Only the caller's own classes (the teacher view; see `LessonFilters.mine`). */
  mine?: boolean;
}

export interface LessonExceptionData {
  id: string;
  schoolId: string;
  lessonId: string;
  originalDate: string;
  type: 'RESCHEDULED' | 'CANCELLED';
  newDate: string | null;
  newStartTime: string | null;
  newEndTime: string | null;
  newRoomId: string | null;
  reason: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lesson?: { name: string };
  newRoom?: { name: string } | null;
}

export interface AvailableTeacher {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  color: string | null;
  instruments: Array<{ id: string; name: string }>;
  availability: {
    day: string;
    time: string;
    available: boolean;
  };
}

export interface AssignReplacementTeacherInput {
  replacementTeacherId: string;
  replacementReason: string;
  /** 1-indexed term week numbers the replacement should cover. */
  weekNumbers: number[];
  /**
   * Opt in to recording coverage for a week that has ALREADY PASSED — a
   * back-office correction ("weeks 2 and 3 were actually covered by someone
   * else"). Omit for normal forward assignments; the server rejects a past week
   * without it.
   *
   * ⚠️ A retroactive assignment is SILENT — the server sends no notification of
   * any kind for a past week, and there is deliberately no client-side flag to
   * change that. Future weeks in the same request still notify as usual.
   */
  allowPast?: boolean;
}

export interface AssignReplacementAllWeeksInput {
  replacementTeacherId: string;
  replacementReason: string;
}

/** One per-week replacement currently in effect on a lesson. */
export interface ReplacementWeekCoverage {
  weekNumber: number;
  /** UTC-midnight ISO date of the occurrence the replacement covers. */
  originalDate: string;
  reason: string | null;
  replacementTeacher: {
    id: string;
    firstName: string;
    lastName: string;
    color: string | null;
  };
  /**
   * Who recorded this coverage. NULL when the user can no longer be resolved —
   * users are hard-deleted and `createdBy` carries no FK, so a dangling id is
   * expected. Always render defensively.
   */
  recordedBy: { firstName: string; lastName: string } | null;
  /**
   * When it was recorded (ISO). This is the ADMIN's action time, NOT the
   * occurrence date — a retroactive edit made today reads "today" here even
   * though the class it describes was weeks ago.
   */
  recordedAt: string | null;
}

/** The full replacement picture for a lesson (legacy whole-term + per-week). */
export interface ReplacementCoverage {
  global: {
    replacementTeacher: {
      id: string;
      firstName: string;
      lastName: string;
      color: string | null;
    };
    reason: string | null;
  } | null;
  weeks: ReplacementWeekCoverage[];
}

/** Assign response includes which weeks (if any) were skipped. */
export interface AssignReplacementResult {
  lesson: Lesson;
  skippedWeeks: number[];
  /**
   * Past weeks recorded WITHOUT notifying anyone (back-office correction).
   * Empty for a normal forward assignment. Surfaced so the UI can confirm the
   * silence explicitly — a silent send is otherwise indistinguishable from a
   * failed one.
   */
  suppressedWeeks: number[];
}

/** Per-week clear response includes which weeks actually had a replacement removed. */
export interface ClearReplacementWeeksResult {
  lesson: Lesson;
  clearedWeeks: number[];
}

// API Response wrapper type (internal use)
// ===========================================
// LESSONS API
// ===========================================
// Note: apiClient already extracts axios response.data, so the return type
// is the ApiResponse wrapper. We then extract .data to get the actual payload.

export const lessonsApi = {
  // Get all lessons with optional filters
  getAll: (filters?: LessonFilters): Promise<Lesson[]> =>
    apiClient
      .get<{ status: string; data: Lesson[] }>('/lessons', { params: filters })
      .then((res) => res.data),

  // Get dated OCCURRENCES with LessonException applied. Distinct from getAll,
  // which returns recurring TEMPLATES — a rescheduled occurrence appears here
  // at its new date, and cancelled ones arrive flagged rather than missing.
  getOccurrences: (params: LessonOccurrencesParams): Promise<LessonOccurrence[]> =>
    apiClient
      .get<{ status: string; data: LessonOccurrence[] }>('/lessons/occurrences', { params })
      .then((res) => res.data),

  // Get a single lesson by ID
  getById: (id: string): Promise<Lesson> =>
    apiClient
      .get<{ status: string; data: Lesson }>(`/lessons/${id}`)
      .then((res) => res.data),

  // Create a new lesson. The result carries the pattern-week clamp warnings (see
  // PatternClampWarning) alongside the lesson — the hooks turn them into toasts.
  create: (data: CreateLessonInput): Promise<LessonWriteResult<Lesson>> =>
    apiClient
      .post<{ status: string; data: Lesson; warnings?: PatternClampWarning[] }>('/lessons', data)
      .then((res) => ({ data: res.data, warnings: res.warnings ?? [] })),

  // Bulk create lessons for multiple terms
  bulkCreate: (data: BulkCreateLessonsInput): Promise<LessonWriteResult<Lesson[]>> =>
    apiClient
      .post<{ status: string; data: Lesson[]; message: string; warnings?: PatternClampWarning[] }>('/lessons/bulk', data)
      .then((res) => ({ data: res.data, warnings: res.warnings ?? [] })),

  // Update a lesson
  update: (id: string, data: UpdateLessonInput): Promise<LessonUpdateResult> =>
    apiClient
      .patch<{
        status: string;
        data: Lesson;
        warnings?: PatternClampWarning[];
        invoicesToAmend?: InvoiceToAmend[];
        invoicesMayNeedAttention?: boolean;
      }>(`/lessons/${id}`, data)
      .then((res) => ({
        data: res.data,
        warnings: res.warnings ?? [],
        invoicesToAmend: res.invoicesToAmend ?? [],
        invoicesMayNeedAttention: res.invoicesMayNeedAttention ?? false,
      })),

  // Delete a lesson (soft delete by default, hard delete with hardDelete=true).
  // force=true lets a BAND lesson be deleted even when hybrid classes reference it: those
  // classes KEEP their combination but their band becomes unassigned ("Band: TBD"), to be
  // reassigned later. The class structure is NOT changed.
  delete: (
    id: string,
    hardDelete: boolean = false,
    force: boolean = false
  ): Promise<{ status: string }> => {
    const params = new URLSearchParams();
    if (hardDelete) params.set('hard', 'true');
    if (force) params.set('force', 'true');
    const query = params.toString();
    return apiClient.delete<{ status: string }>(`/lessons/${id}${query ? `?${query}` : ''}`);
  },

  // ===========================================
  // BAND LESSON OPERATIONS
  // ===========================================

  // Get all active band lessons (optionally filtered by term)
  getBandLessons: (termId?: string): Promise<Lesson[]> =>
    apiClient
      .get<{ status: string; data: Lesson[] }>('/lessons/band', {
        params: termId ? { termId } : undefined,
      })
      .then((res) => res.data),

  // Preview which weeks a band lesson falls on within a term
  getBandWeeksPreview: (bandLessonId: string, termId: string): Promise<number[]> =>
    apiClient
      .get<{ status: string; data: number[] }>(`/lessons/band/${bandLessonId}/weeks`, {
        params: { termId },
      })
      .then((res) => res.data),

  // ===========================================
  // BAND TERM OPERATIONS
  // ===========================================

  // Get BandTerm configuration for a band lesson
  getBandTerm: (lessonId: string, termId: string): Promise<BandTerm | null> =>
    apiClient
      .get<{ status: string; data: BandTerm | null }>(`/lessons/${lessonId}/band-term`, {
        params: { termId },
      })
      .then((res) => res.data),

  // Create or update BandTerm configuration for a band lesson
  upsertBandTerm: (lessonId: string, data: UpsertBandTermInput): Promise<BandTerm> =>
    apiClient
      .put<{ status: string; data: BandTerm }>(`/lessons/${lessonId}/band-term`, data)
      .then((res) => res.data),

  // Delete BandTerm configuration for a band lesson
  deleteBandTerm: (lessonId: string, termId: string): Promise<{ status: string }> =>
    apiClient.delete<{ status: string }>(`/lessons/${lessonId}/band-term`, {
      params: { termId },
    }),

  // ===========================================
  // ENROLLMENT OPERATIONS
  // ===========================================

  // Get messageable class members (parents + students with accounts)
  getClassMembers: (lessonId: string): Promise<ClassMembers> =>
    apiClient
      .get<{ status: string; data: ClassMembers }>(`/lessons/${lessonId}/class-members`)
      .then((res) => res.data),

  // Get enrollments for a lesson
  getEnrollments: (lessonId: string): Promise<LessonEnrollment[]> =>
    apiClient
      .get<{ status: string; data: LessonEnrollment[] }>(`/lessons/${lessonId}/enrollments`)
      .then((res) => res.data),

  // Enroll a single student
  enrollStudent: (
    lessonId: string,
    studentId: string,
    enrollInAllTerms?: boolean,
    startWeek?: number | null
  ): Promise<EnrollStudentResult> =>
    apiClient
      .post<{
        status: string;
        data: LessonEnrollment;
        alreadyInvoiced?: AlreadyInvoicedEnrolment[];
        invoicesMayNeedAttention?: boolean;
      }>(
        `/lessons/${lessonId}/enrollments`,
        { studentId, enrollInAllTerms, startWeek }
      )
      .then((res) => ({
        enrollment: res.data,
        alreadyInvoiced: res.alreadyInvoiced ?? [],
        invoicesMayNeedAttention: res.invoicesMayNeedAttention ?? false,
      })),

  // Bulk enroll students
  bulkEnroll: (
    lessonId: string,
    studentIds: string[],
    enrollInAllTerms?: boolean,
    startWeek?: number | null
  ): Promise<BulkEnrollResult> =>
    apiClient
      .post<{
        status: string;
        data: LessonEnrollment[];
        alreadyInvoiced?: AlreadyInvoicedEnrolment[];
        invoicesMayNeedAttention?: boolean;
      }>(
        `/lessons/${lessonId}/enrollments/bulk`,
        { studentIds, enrollInAllTerms, startWeek }
      )
      .then((res) => ({
        enrollments: res.data,
        alreadyInvoiced: res.alreadyInvoiced ?? [],
        invoicesMayNeedAttention: res.invoicesMayNeedAttention ?? false,
      })),

  // Change the week one enrolled student joins (null = the class's own start)
  updateEnrollment: (
    lessonId: string,
    studentId: string,
    startWeek: number | null
  ): Promise<EnrollmentUpdateResult> =>
    apiClient
      .patch<{
        status: string;
        data: LessonEnrollment;
        invoicesToAmend?: InvoiceToAmend[];
        invoicesMayNeedAttention?: boolean;
      }>(
        `/lessons/${lessonId}/enrollments/${studentId}`,
        { startWeek }
      )
      .then((res) => ({
        enrollment: res.data,
        invoicesToAmend: res.invoicesToAmend ?? [],
        invoicesMayNeedAttention: res.invoicesMayNeedAttention ?? false,
      })),

  // Unenroll a student
  unenrollStudent: (lessonId: string, studentId: string): Promise<{ status: string }> =>
    apiClient.delete<{ status: string }>(
      `/lessons/${lessonId}/enrollments/${studentId}`
    ),

  // Toggle waitlist for a student
  // ===========================================
  // AVAILABILITY CHECKS
  // ===========================================

  // Check room availability
  checkRoomAvailability: (
    roomId: string,
    dayOfWeek: number,
    startTime: string,
    endTime: string,
    excludeLessonId?: string
  ): Promise<AvailabilityResult> =>
    apiClient
      .get<{ status: string; data: AvailabilityResult }>('/lessons/check/room-availability', {
        params: { roomId, dayOfWeek, startTime, endTime, excludeLessonId },
      })
      .then((res) => res.data),

  // Check teacher availability
  checkTeacherAvailability: (
    teacherId: string,
    dayOfWeek: number,
    startTime: string,
    endTime: string,
    excludeLessonId?: string
  ): Promise<AvailabilityResult> =>
    apiClient
      .get<{ status: string; data: AvailabilityResult }>(
        '/lessons/check/teacher-availability',
        {
          params: { teacherId, dayOfWeek, startTime, endTime, excludeLessonId },
        }
      )
      .then((res) => res.data),

  // Check enrollment capacity
  checkCapacity: (lessonId: string): Promise<CapacityResult> =>
    apiClient
      .get<{ status: string; data: CapacityResult }>(`/lessons/${lessonId}/capacity`)
      .then((res) => res.data),

  // ===========================================
  // RESCHEDULE OPERATIONS
  // ===========================================

  // Check for conflicts before rescheduling
  checkRescheduleConflicts: (
    lessonId: string,
    input: ConflictCheckInput
  ): Promise<ConflictCheckResult> =>
    apiClient
      .get<{ status: string; data: ConflictCheckResult }>(
        `/lessons/${lessonId}/check-conflicts`,
        { params: input }
      )
      .then((res) => res.data),

  // Get related lessons across terms (same name, teacher, room, time)
  getRelatedLessons: (lessonId: string): Promise<RelatedLesson[]> =>
    apiClient
      .get<{ status: string; data: RelatedLesson[] }>(`/lessons/${lessonId}/related`)
      .then((res) => res.data),

  // Reschedule a lesson
  reschedule: (lessonId: string, input: RescheduleInput): Promise<Lesson> =>
    apiClient
      .post<{ status: string; data: Lesson }>(`/lessons/${lessonId}/reschedule`, input)
      .then((res) => res.data),

  // Check room conflicts for an "all classes" location change
  checkChangeRoomConflicts: (
    lessonId: string,
    params: { roomId: string; applyToAllTerms?: boolean }
  ): Promise<ChangeRoomConflictResult> =>
    apiClient
      .get<{ status: string; data: ChangeRoomConflictResult }>(
        `/lessons/${lessonId}/check-room-conflicts`,
        { params }
      )
      .then((res) => res.data),

  // Change a recurring lesson's room ("all classes" scope)
  changeRoom: (lessonId: string, input: ChangeLessonRoomInput): Promise<Lesson> =>
    apiClient
      .post<{ status: string; data: Lesson }>(`/lessons/${lessonId}/change-room`, input)
      .then((res) => res.data),

  // ===========================================
  // SINGLE-OCCURRENCE OPERATIONS
  // ===========================================

  // Check conflicts for a single-occurrence reschedule
  checkSingleOccurrenceConflicts: (
    lessonId: string,
    input: SingleOccurrenceConflictCheckInput
  ): Promise<ConflictCheckResult> =>
    apiClient
      .get<{ status: string; data: ConflictCheckResult }>(
        `/lessons/${lessonId}/check-single-conflicts`,
        { params: input }
      )
      .then((res) => res.data),

  // Reschedule a single occurrence
  rescheduleSingleOccurrence: (
    lessonId: string,
    input: SingleOccurrenceRescheduleInput
  ): Promise<LessonExceptionData> =>
    apiClient
      .post<{ status: string; data: LessonExceptionData }>(
        `/lessons/${lessonId}/reschedule-occurrence`,
        input
      )
      .then((res) => res.data),

  // Cancel a single occurrence
  cancelSingleOccurrence: (
    lessonId: string,
    input: CancelOccurrenceInput
  ): Promise<LessonExceptionData> =>
    apiClient
      .post<{ status: string; data: LessonExceptionData }>(
        `/lessons/${lessonId}/cancel-occurrence`,
        input
      )
      .then((res) => res.data),

  // Get exceptions for a lesson
  getExceptions: (lessonId: string): Promise<LessonExceptionData[]> =>
    apiClient
      .get<{ status: string; data: LessonExceptionData[] }>(
        `/lessons/${lessonId}/exceptions`
      )
      .then((res) => res.data),

  // Delete an exception (restores original occurrence)
  deleteException: (
    lessonId: string,
    exceptionId: string
  ): Promise<{ status: string }> =>
    apiClient.delete<{ status: string }>(
      `/lessons/${lessonId}/exceptions/${exceptionId}`
    ),

  // ===========================================
  // REPLACEMENT TEACHER OPERATIONS
  // ===========================================

  // Get available replacement teachers for a lesson, optionally filtered to
  // teachers free on ALL the given weeks.
  getAvailableReplacementTeachers: (
    lessonId: string,
    weekNumbers?: number[]
  ): Promise<AvailableTeacher[]> =>
    apiClient
      .get<{ status: string; data: AvailableTeacher[] }>(
        `/lessons/${lessonId}/available-replacement-teachers`,
        weekNumbers && weekNumbers.length > 0
          ? { params: { weeks: weekNumbers.join(',') } }
          : undefined
      )
      .then((res) => res.data),

  // Get current replacement coverage (legacy global + per-week)
  getReplacementCoverage: (lessonId: string): Promise<ReplacementCoverage> =>
    apiClient
      .get<{ status: string; data: ReplacementCoverage }>(
        `/lessons/${lessonId}/replacement-coverage`
      )
      .then((res) => res.data),

  // Assign replacement teacher for specific weeks
  assignReplacementTeacher: (
    lessonId: string,
    data: AssignReplacementTeacherInput
  ): Promise<AssignReplacementResult> =>
    apiClient
      .patch<{
        status: string;
        data: Lesson;
        skippedWeeks: number[];
        suppressedWeeks?: number[];
      }>(`/lessons/${lessonId}/assign-replacement-teacher`, data)
      .then((res) => ({
        lesson: res.data,
        skippedWeeks: res.skippedWeeks ?? [],
        suppressedWeeks: res.suppressedWeeks ?? [],
      })),

  // Assign replacement teacher to all remaining weeks (shortcut)
  assignReplacementAllWeeks: (
    lessonId: string,
    data: AssignReplacementAllWeeksInput
  ): Promise<AssignReplacementResult> =>
    apiClient
      .patch<{ status: string; data: Lesson; skippedWeeks: number[] }>(
        `/lessons/${lessonId}/assign-replacement-all-weeks`,
        data
      )
      // "All remaining weeks" is non-past by construction, so nothing can be
      // suppressed on this path — always [].
      .then((res) => ({
        lesson: res.data,
        skippedWeeks: res.skippedWeeks ?? [],
        suppressedWeeks: [],
      })),

  // Reinstate the regular teacher for specific weeks
  clearReplacementWeeks: (lessonId: string, weekNumbers: number[]): Promise<ClearReplacementWeeksResult> =>
    apiClient
      .delete<{ status: string; data: Lesson; clearedWeeks: number[] }>(
        `/lessons/${lessonId}/replacement-weeks`,
        { data: { weekNumbers } }
      )
      .then((res) => ({ lesson: res.data, clearedWeeks: res.clearedWeeks ?? [] })),

  // Clear replacement teacher from lesson (all weeks + legacy global)
  clearReplacementTeacher: (lessonId: string): Promise<Lesson> =>
    apiClient
      .delete<{ status: string; data: Lesson }>(`/lessons/${lessonId}/clear-replacement-teacher`)
      .then((res) => res.data),
};

// ===========================================
// HELPER FUNCTIONS
// ===========================================

/**
 * Get day of week name from number
 */
export const getDayName = (dayOfWeek: number): string => {
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  return days[dayOfWeek] || '';
};

/**
 * Get short day name from number
 */
export const getShortDayName = (dayOfWeek: number): string => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days[dayOfWeek] || '';
};

/**
 * Format time for display (e.g., "09:00" -> "9:00 AM")
 */
export const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/**
 * Get lesson type color for chip
 */
export const getLessonTypeColor = (
  type: string
): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
  switch (type) {
    case 'INDIVIDUAL':
      return 'primary';
    case 'GROUP':
      return 'success';
    case 'BAND':
      return 'warning';
    case 'HYBRID':
      return 'info';
    default:
      return 'primary';
  }
};

/**
 * Calculate end time from start time and duration
 */
export const calculateEndTime = (
  startTime: string,
  durationMins: number
): string => {
  const [hours, minutes] = startTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + durationMins;
  const endHours = Math.floor(totalMinutes / 60) % 24;
  const endMinutes = totalMinutes % 60;
  return `${endHours.toString().padStart(2, '0')}:${endMinutes
    .toString()
    .padStart(2, '0')}`;
};
